/**
 * NotasPanel — componente reutilizável de notas/anotações.
 * Vincula-se a qualquer recurso (demanda, contrato, evento) via props.
 */

import { useState, useEffect, useCallback } from 'react';
import { api } from '../lib/api';
import type { Anotacao, RecursoTipo } from '../lib/types';
import { useAuth } from '../lib/auth';

interface NotasPanelProps {
  recursoTipo: RecursoTipo;
  recursoId: string;
}

export default function NotasPanel({ recursoTipo, recursoId }: NotasPanelProps) {
  const { user } = useAuth();
  const [notas, setNotas] = useState<Anotacao[]>([]);
  const [loading, setLoading] = useState(true);
  const [texto, setTexto] = useState('');
  const [sending, setSending] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editTexto, setEditTexto] = useState('');
  const [visivelCliente, setVisivelCliente] = useState(false);
  const [error, setError] = useState('');
  const isStaff = user?.papel !== 'Cliente';

  const fetchNotas = useCallback(async () => {
    try {
      const res = await api.anotacoes.list(recursoTipo, recursoId);
      setNotas(res.data);
    } catch {
      setError('Erro ao carregar notas');
    } finally {
      setLoading(false);
    }
  }, [recursoTipo, recursoId]);

  useEffect(() => {
    fetchNotas();
  }, [fetchNotas]);

  const handleCreate = async () => {
    if (!texto.trim()) return;
    setSending(true);
    setError('');
    try {
      await api.anotacoes.create({ texto: texto.trim(), recursoTipo, recursoId, visivelCliente });
      setTexto('');
      setVisivelCliente(false);
      await fetchNotas();
    } catch {
      setError('Erro ao salvar nota');
    } finally {
      setSending(false);
    }
  };

  const handleUpdate = async (id: string) => {
    if (!editTexto.trim()) return;
    try {
      await api.anotacoes.update(id, editTexto.trim());
      setEditingId(null);
      setEditTexto('');
      await fetchNotas();
    } catch {
      setError('Erro ao editar nota');
    }
  };

  const handleDelete = async (id: string) => {
    if (!confirm('Remover esta nota?')) return;
    try {
      await api.anotacoes.delete(id);
      await fetchNotas();
    } catch {
      setError('Erro ao remover nota');
    }
  };

  const canEditNote = (nota: Anotacao) =>
    nota.autorEmail === user?.email || user?.papel === 'Coordenação';

  const formatDate = (iso: string) => {
    try {
      return new Date(iso).toLocaleString('pt-BR', { dateStyle: 'short', timeStyle: 'short' });
    } catch {
      return iso;
    }
  };

  if (loading) {
    return <div className="text-sm text-gray-400 py-4">Carregando notas...</div>;
  }

  return (
    <div className="space-y-3">
      {error && (
        <div className="text-xs text-red-500 bg-red-50 dark:bg-red-900/20 px-3 py-1.5 rounded">
          {error}
        </div>
      )}

      {/* Input nova nota */}
      <div className="space-y-1.5">
        <div className="flex gap-2">
          <textarea
            value={texto}
            onChange={(e) => setTexto(e.target.value)}
            placeholder="Adicionar nota..."
            rows={2}
            className="flex-1 text-sm rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
            onKeyDown={(e) => {
              if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) {
                e.preventDefault();
                handleCreate();
              }
            }}
          />
          <button
            onClick={handleCreate}
            disabled={sending || !texto.trim()}
            className="self-end px-3 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-50 rounded-lg transition-colors"
          >
            {sending ? '...' : 'Salvar'}
          </button>
        </div>
        {isStaff && recursoTipo !== 'geral' && (
          <label className="flex items-center gap-1.5 text-[11px] text-gray-500 cursor-pointer select-none">
            <input type="checkbox" checked={visivelCliente} onChange={(e) => setVisivelCliente(e.target.checked)}
              className="rounded border-gray-300 text-blue-600 focus:ring-blue-500 w-3.5 h-3.5" />
            Visível para o cliente
          </label>
        )}
      </div>

      {/* Lista de notas */}
      {notas.length === 0 ? (
        <p className="text-xs text-gray-400 italic">Nenhuma nota ainda.</p>
      ) : (
        <div className="space-y-2">
          {notas.map((nota) => (
            <div
              key={nota.id}
              className="border border-gray-100 dark:border-gray-700 rounded-lg px-3 py-2 bg-gray-50/50 dark:bg-gray-800/50"
            >
              {editingId === nota.id ? (
                <div className="space-y-2">
                  <textarea
                    value={editTexto}
                    onChange={(e) => setEditTexto(e.target.value)}
                    rows={2}
                    className="w-full text-sm rounded border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-800 px-2 py-1 focus:outline-none focus:ring-1 focus:ring-blue-500 resize-none"
                  />
                  <div className="flex gap-2">
                    <button
                      onClick={() => handleUpdate(nota.id)}
                      className="text-xs text-blue-600 hover:text-blue-700 font-medium"
                    >
                      Salvar
                    </button>
                    <button
                      onClick={() => { setEditingId(null); setEditTexto(''); }}
                      className="text-xs text-gray-400 hover:text-gray-600"
                    >
                      Cancelar
                    </button>
                  </div>
                </div>
              ) : (
                <>
                  <p className="text-sm text-gray-800 dark:text-gray-200 whitespace-pre-wrap">
                    {nota.texto}
                  </p>
                  <div className="flex items-center justify-between mt-1.5">
                    <span className="text-[11px] text-gray-400">
                      {nota.autorNome} · {formatDate(nota.criadoEm)}
                      {nota.editadoEm && ' (editada)'}
                      {nota.visivelCliente && <span className="ml-1.5 text-green-600 font-medium">· visível p/ cliente</span>}
                    </span>
                    {canEditNote(nota) && (
                      <div className="flex gap-2">
                        {isStaff && recursoTipo !== 'geral' && (
                          <button
                            onClick={async () => {
                              try {
                                await api.anotacoes.toggleVisibility(nota.id, !nota.visivelCliente);
                                await fetchNotas();
                              } catch { setError('Erro ao alterar visibilidade'); }
                            }}
                            className="text-[11px] text-gray-400 hover:text-green-600"
                          >
                            {nota.visivelCliente ? 'Ocultar do cliente' : 'Mostrar ao cliente'}
                          </button>
                        )}
                        <button
                          onClick={() => { setEditingId(nota.id); setEditTexto(nota.texto); }}
                          className="text-[11px] text-gray-400 hover:text-blue-500"
                        >
                          Editar
                        </button>
                        <button
                          onClick={() => handleDelete(nota.id)}
                          className="text-[11px] text-gray-400 hover:text-red-500"
                        >
                          Remover
                        </button>
                      </div>
                    )}
                  </div>
                </>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
